import { Logger } from 'services';
import { appEnvironment, inImpersonateMode } from 'helpers/constants';

/**
 * If the user is using the chrome extension, the auth tokens
 * will be saved to chrome storage instead of local storage
 */
const tokenKeys = ['accessToken', 'refreshToken'];

function pullFromChromeStorage(key: string) {
  return new Promise((resolve) => {
    chrome.storage.local.get([key], function (obj) {
      resolve(obj[key]);
    });
  });
}

/**
 * Uses chrome storage if all conditions are true:
 * - Is using the chrome extension
 * - Is one of our auth token keys
 * - Chrome storage exists / is available
 * @param key
 */
function usesChromeStorage(key: string) {
  return appEnvironment === 'chrome' && tokenKeys.includes(key) && !!chrome.storage;
}

/**
 * Retrives a value from the storage
 * @param key
 */
async function get(key: string) {
  if (inImpersonateMode) {
    return new Promise((resolve, reject) => {
      try {
        const data = sessionStorage.getItem(key) || '';
        resolve(data);
      } catch (error) {
        Logger.log(`Error reading ${key} from localStorage`);
        reject(error);
      }
    });
  } else if (usesChromeStorage(key)) {
    return pullFromChromeStorage(key).then((result) => {
      return result;
    });
  } else {
    return new Promise((resolve, reject) => {
      try {
        const data = localStorage.getItem(key) || '';
        resolve(data);
      } catch (error) {
        Logger.log(`Error reading ${key} from localStorage`);
        reject(error);
      }
    });
  }
}

/**
 * Sets a value on the storage
 * @param key
 * @param value
 */
async function set(key: string, value: string) {
  if (inImpersonateMode) {
    return sessionStorage.setItem(key, value);
  } else if (usesChromeStorage(key)) {
    return chrome.storage.local.set({ [key]: value });
  } else {
    return localStorage.setItem(key, value);
  }
}

/**
 * Removes a value on the storage
 * @param key
 */
async function remove(key: string) {
  if (inImpersonateMode) {
    return new Promise((resolve) => {
      sessionStorage.removeItem(key);
      resolve(key);
    });
  } else if (usesChromeStorage(key)) {
    return chrome.storage.local.remove(key, () => {
      return key;
    });
  } else {
    return new Promise((resolve) => {
      localStorage.removeItem(key);
      resolve(key);
    });
  }
}

/**
 * Clears storage
 * @param key
 */
async function clear() {
  if (inImpersonateMode) {
    window.close();
  } else {
    const clearLocalStorage = new Promise((resolve) => {
      localStorage.clear();
      resolve();
    });

    const clearAsyncStorage = new Promise((resolve) => {
      if (appEnvironment === 'chrome') {
        return chrome.storage.local.clear(() => {
          resolve();
        });
      }
      resolve();
    });

    return Promise.all([clearLocalStorage, clearAsyncStorage]);
  }
}

export const AsyncStorage = {
  get,
  set,
  clear,
  remove,
};
